import React, { useState, useMemo } from 'react'
import {
  InputGroup,
  Form,
  Button,
  Card,
  Collapse,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import {
  FaFilter,
  FaArrowUp,
  FaArrowDown,
  FaClockRotateLeft
} from 'react-icons/fa6'
import { FaSearch } from 'react-icons/fa'
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from 'react-icons/md'
import './Margins.scss'
import { formatCurrency } from '../../../../../../utils/functions'

interface Ingrediente {
  name: string
  costContribution: number
}
interface Plato {
  id: number
  name: string
  imageUrl: string
  cost: number
  price: number
  margin: number       // price – cost
  trendUp: boolean     // ↑ si true, ↓ si false
  lastUpdate: string   // ISO string
  ingredients: Ingrediente[]
}

// datos de prueba con &sig para cache estable
const fakeData: Plato[] = [
    {
      id: 1,
      name: 'Strozzapreti Antonella',
      imageUrl:
        'https://static.wixstatic.com/media/3fd533_88da344ed68944d2bf9fb2a2666bfdee~mv2.jpg',
      // Costo total aproximado sumando todas las contribuciones
      cost: 9950,       
      price: 18000,    
      margin: 8050,    
      trendUp: true,
      lastUpdate: '2025-05-12T10:30:00Z',
      ingredients: [
        { name: 'Alcachofa en conserva (1kg)', costContribution: 2000 },
        { name: 'Crema de leche (500ml)',    costContribution:  800 },
        { name: 'Prosciutto Pharma (200g)',   costContribution: 5000 },
        { name: 'Harina italiana (1kg)',      costContribution: 1500 },
        { name: 'Ajo',                        costContribution:  150 },
        { name: 'Aceite de oliva',            costContribution:  300 },
        { name: 'Cebolla',                    costContribution:  200 },
      ],
    },
    {
      id: 2,
      name: 'Pizza Vegetariana',
      imageUrl:
        'https://static.wixstatic.com/media/9dc409_913822b1934449228b46fcae1695068a~mv2.jpg',
      cost: 8400,
      price: 15000,
      margin: 6600,
      trendUp: false,
      lastUpdate: '2025-05-10T14:15:00Z',
      ingredients: [
        { name: 'Masa (300g)',          costContribution:  500 },
        { name: 'Mozzarella (300g)',    costContribution: 4000 },
        { name: 'Salsa de tomate (200g)', costContribution:  800 },
        { name: 'Pimientos',            costContribution:  600 },
        { name: 'Cebolla',              costContribution:  400 },
        { name: 'Champiñones',          costContribution: 1200 },
        { name: 'Calabacín',            costContribution:  900 },
      ],
    },
    {
      id: 3,
      name: 'Gnocchi Emperatore',
      imageUrl:
        'https://static.wixstatic.com/media/9dc409_94fef581407d4bc782a5084369c2adcb~mv2.jpg',
      cost: 7500,
      price: 20000,
      margin: 12500,
      trendUp: true,
      lastUpdate: '2025-05-11T09:20:00Z',
      ingredients: [
        { name: 'Papa (1kg)',           costContribution: 1000 },
        { name: 'Salsa de queso (300g)', costContribution: 3000 },
        { name: 'Carne molida (250g)',   costContribution: 3000 },
        { name: 'Albahaca fresca',       costContribution:  200 },
        { name: 'Mantequilla',           costContribution:  300 },
        { name: 'Nuez moscada',          costContribution:  100 },
      ],
    },
    {
      id: 4,
      name: 'Ravioli Frutti di Mare',
      imageUrl:
        'https://static.wixstatic.com/media/9dc409_15490bb2c48044bea8802c912093ef98~mv2.jpg',
      cost: 10400,
      price: 25000,
      margin: 14600,
      trendUp: false,
      lastUpdate: '2025-05-09T17:45:00Z',
      ingredients: [
        { name: 'Masa para pasta (300g)',    costContribution:  500 },
        { name: 'Mezcla frutos de mar (500g)', costContribution: 8000 },
        { name: 'Ricotta (200g)',            costContribution: 1500 },
        { name: 'Ajo',                       costContribution:  150 },
        { name: 'Perejil fresco',            costContribution:  100 },
        { name: 'Ralladura de limón',        costContribution:  150 },
      ],
    },
  ]

  
  export const Margins: React.FC = () => {
    const [searchText, setSearchText] = useState('')
    const [openId, setOpenId] = useState<number | null>(null)
  
    const filtered = useMemo(
      () =>
        fakeData.filter(d =>
          d.name.toLowerCase().includes(searchText.toLowerCase())
        ),
      [searchText]
    )
  
  
    return (
      <div className="margins">
        <div className="margins__cmdbar">
          <InputGroup className="margins__search">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Buscar plato…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </InputGroup>
          <OverlayTrigger placement="bottom" overlay={<Tooltip>Filtrar</Tooltip>}>
            <Button variant="outline-secondary" className="margins__filter">
              <FaFilter />
            </Button>
          </OverlayTrigger>
        </div>
  
        <div className="margins__stack">
          {filtered.map(d => {
            const isOpen = openId === d.id
            const beneficio = d.price - d.cost
            const margenPct = (beneficio / d.price) * 100
            const trendClass = d.trendUp ? 'profit' : 'loss'
  
            return (
              <Card
                key={d.id}
                className={`margins__card margins__card--${trendClass}`}
              >
                <Card.Body className="margins__card-body">
                  <div className="margins__top-row">
                    <img
                      src={d.imageUrl}
                      alt={d.name}
                      className="margins__img"
                      onError={e => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src =
                          'https://via.placeholder.com/120?text=Sin+imagen'
                      }}
                    />
  
                    <div className="margins__main">
                      <a href="#" className="margins__title">
                        {d.name}
                      </a>
  
                      <div className="margins__fields">
                        <div className="field">
                          <div className="label">PVP </div>
                          <div className="value">{formatCurrency(d.price)}</div>
                        </div>
                        <div className="field">
                          <div className="label">Coste</div>
                          <div className="value">{formatCurrency(d.cost)}</div>
                        </div>
                        <div className="field">
                          <div className="label">Beneficio</div>
                          <div className="value">{formatCurrency(beneficio)}</div>
                        </div>
                        <div className="field">
                          <div className="label">Margen</div>
                          <div className="value">
                            {margenPct.toFixed(0)}%{' '}
                            {d.trendUp ? <FaArrowUp /> : <FaArrowDown />}
                          </div>
                        </div>
                      </div>
                    </div>
  
                    <Button
                      variant="link"
                      className="margins__toggle"
                      onClick={() => setOpenId(isOpen ? null : d.id)}
                    >
                      {isOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                    </Button>
                  </div>
  
                  <Collapse in={isOpen}>
                    <div className="margins__details">
                      <h6>
                        Ingredientes 
                        <span className='margins__last-update'>
                      <FaClockRotateLeft/>:{' '}
                      {new Date(d.lastUpdate).toLocaleDateString('es-ES')}
                      </span>
                      </h6>
                      <ul>
                        {d.ingredients.map((ing, i) => (
                          <li key={i}>
                            <span>{ing.name} </span>
                            <span> {formatCurrency(ing.costContribution)} </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Collapse>
                </Card.Body>
              </Card>
            )
          })}
        </div>
      </div>
    )
  }
  
  export default Margins
